/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCallback, useState } from "react";
import { BlurredImage, CosmosAsset, CosmosTagProps, OptimizedCosmosAssetFragment } from "@components/elements";

import { styled } from "./ContentLargeImage.Media.styles";
import { cx } from "@panda/css";
import { BlogTagFragment } from "@cosmos/hygraph-schema";

export interface ContentLargeImageMediaProps {
	asset?: OptimizedCosmosAssetFragment | null;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	blurredImage?: OptimizedCosmosAssetFragment | null;
	disableAutoplay?: boolean | null;
	className?: string;
	isFirstSection?: boolean;
}

export const ContentLargeImageMedia = ({
	asset,
	tags,
	blurredImage,
	disableAutoplay,
	className,
	isFirstSection,
}: ContentLargeImageMediaProps): React.JSX.Element | null => {
	const hasDistinctBlurredImage = Boolean(blurredImage?.url) && blurredImage?.url !== asset?.url;
	const needsSharedSource = !hasDistinctBlurredImage;
	const [mediaElement, setMediaElement] = useState<HTMLImageElement | HTMLVideoElement | null>(null);

	const handleMediaRef = useCallback((el: HTMLImageElement | HTMLVideoElement | null) => {
		setMediaElement(el);
	}, []);

	if (!asset) return null;

	const finalBlurredImage = hasDistinctBlurredImage ? blurredImage : asset;

	const sizedAsset: OptimizedCosmosAssetFragment = {
		...(asset || {}),
		url: asset?.url || "",
		width: 900,
	};

	return (
		<div className={cx("content-large-image-hero-image", styled, className)}>
			<div className="image-holder">
				<BlurredImage
					image={finalBlurredImage}
					sourceElement={needsSharedSource ? mediaElement : undefined}
					width={900}
					height={506.25}
					blurPixels={80}
				/>
				<div className="asset-wrap">
					<CosmosAsset
						className="cosmos-asset"
						asset={sizedAsset}
						tags={tags}
						disableAutoplay={disableAutoplay || false}
						eager={isFirstSection}
						mediaRef={needsSharedSource ? handleMediaRef : undefined}
						crossOrigin={needsSharedSource ? "anonymous" : undefined}
						sizes="(max-width: 1024px) 100vw, 50vw"
					/>
				</div>
			</div>
		</div>
	);
};
